/**
 * What a video export needs from the host, as types the core can hold.
 *
 * Phase 5 could already re-render a performance deterministically, but it got
 * the pictures out as PNGs — `canvas.toBlob('image/png')` per frame, which
 * measured **0.97 frames per second**. A four-minute export at 60 fps is 14 400
 * frames; at that rate it is four hours, and the format was the reason. PNG is
 * lossless, per-frame, and entropy-coded on the CPU; nothing about it is meant
 * for video.
 *
 * So the frames now go straight into a video encoder. Everything in this file is
 * the *shape* of that, with none of the machinery: no `VideoFrame`, no
 * `VideoEncoder`, no canvas, no DOM. Those live in `src/view/export/`, and this
 * layer holds them at arm's length through the three interfaces below — which is
 * the same arrangement `RenderTarget` already uses for drawing, and it is what
 * keeps `src/visual/` compiling with `lib: ["ES2022"], types: []`.
 *
 * ## The contract between the two halves
 *
 * `FrameSource` and `VideoEncoderAdapter` are a **matched pair**. The source
 * produces a `CapturedFrame`, the core carries it without looking inside, and
 * the adapter consumes it. The core cannot check that they agree, so a host must
 * supply both from the same place — which is exactly what
 * `createWebCodecsExport` does.
 */

/**
 * A frame that has been captured and not yet released.
 *
 * Opaque on purpose: the core knows when a frame exists, which frame it is and
 * when it stops being needed, and nothing else. `close` must be called exactly
 * once — a `VideoFrame` holds a GPU allocation, and a few hundred unreleased
 * ones will exhaust a browser's frame pool and stall the export.
 */
export interface CapturedFrame {
  readonly index: number;
  /** Microseconds from the start of the export. See `frameTimestampMicros`. */
  readonly timestampMicros: number;
  close(): void;
}

/** Captures whatever the render target last drew. */
export interface FrameSource {
  /**
   * Takes the current contents of the drawing surface.
   *
   * Returns null when capture is not possible, which the session treats as a
   * dropped frame rather than as a failure — a host without the API should get a
   * clear diagnostic, not an exception in the middle of a render.
   */
  capture(index: number, timestampMicros: number): Promise<CapturedFrame | null>;
  /** How many captured frames are still open. Watched for leaks. */
  readonly liveFrames: number;
  /** How the frames are being taken, for the report. */
  readonly method: string;
  dispose(): void;
}

/** One encoded chunk, as much of it as the core needs to pass it on. */
export interface EncodedVideoChunkLike {
  readonly type: 'key' | 'delta';
  readonly timestampMicros: number;
  readonly durationMicros: number | null;
  readonly byteLength: number;
  /** Copies the payload into `destination`, which must be big enough. */
  copyTo(destination: Uint8Array): void;
  /**
   * The encoder's decoder configuration, when it supplied one.
   *
   * Opaque on purpose, and carried for the same reason the frame is: a muxer
   * needs it — an H.264 track without its `avcC` is unplayable — but its shape
   * belongs to WebCodecs, and this layer's job is to hand it from the encoder
   * that produced it to the muxer that wants it. Present on the first chunk
   * only, which is how WebCodecs delivers it.
   */
  readonly decoderConfig?: unknown;
}

export type VideoEncoderState = 'unconfigured' | 'configured' | 'closed' | 'errored';

export interface VideoEncodeConfig {
  readonly codec: string;
  readonly width: number;
  readonly height: number;
  readonly framerate: number;
  readonly bitrate: number;
  /** Hardware where available, but never *required* — see `selectVideoCodec`. */
  readonly hardwareAcceleration?: 'no-preference' | 'prefer-hardware' | 'prefer-software';
  readonly latencyMode?: 'quality' | 'realtime';
  /**
   * How an H.264 stream is packaged.
   *
   * `annexb` prefixes each unit with a start code and is what a raw `.h264`
   * stream needs to be inspectable on its own. `avc` is length-prefixed with
   * the parameter sets carried separately in an `avcC` box, and it is the only
   * form an MP4 can hold — a container fed Annex B writes a track that every
   * demuxer rejects. Phase 7A had no container and preferred the former; a
   * muxed export asks for the latter.
   */
  readonly bitstreamFormat?: 'annexb' | 'avc';
}

/**
 * The encoder, as the core sees it.
 *
 * Deliberately mirrors WebCodecs' shape without being it, so a test can supply a
 * fake and a future host could supply something else entirely.
 */
export interface VideoEncoderAdapter {
  readonly state: VideoEncoderState;
  /** Frames accepted but not yet encoded. The backpressure signal. */
  readonly queueSize: number;
  /**
   * Chunks emitted so far, when the host can say.
   *
   * Needed to tell "busy" apart from "not started". A hardware encoder buffers
   * a pipeline's worth of frames before it emits anything — around two dozen on
   * the machine this was written on — and during that window its queue only
   * grows. Backpressure applied blindly there is a deadlock: the queue cannot
   * fall until the encoder emits, and the encoder cannot emit until it is given
   * more frames than the limit allows. Zero means the pipeline is still filling
   * and waiting cannot help.
   *
   * Optional so an adapter that cannot count is still valid; a session that
   * gets `undefined` keeps the plain limit.
   */
  readonly producedChunks?: number;
  configure(config: VideoEncodeConfig): Promise<void>;
  /** Takes a frame. Does **not** close it; the caller still owns it. */
  encode(frame: CapturedFrame, options: { readonly keyFrame: boolean }): void;
  flush(): Promise<void>;
  close(): void;
}

// ---------------------------------------------------------------------------
// Codec selection
// ---------------------------------------------------------------------------

export interface VideoCodecCandidate {
  /** For the report and the UI. */
  readonly name: string;
  /** The codec string an encoder is configured with. */
  readonly codec: string;
  /** Which container Phase 7B will need to mux this into. */
  readonly container: 'mp4' | 'webm';
}

/**
 * The codecs worth trying, best first.
 *
 * Ordered by what an exported file is most likely to be *playable* as rather
 * than by compression: H.264 in MP4 opens everywhere including phones and
 * editing software, VP9 in WebM is the reliable open fallback, AV1 is better
 * than both and least widely encodable today. Nothing here is assumed to exist —
 * `selectVideoCodec` asks the host about each in turn.
 *
 * The AVC string is `avc1.640028`: High profile, level 4.0, which covers 1080p60
 * and is what most hardware encoders actually expose. A lower level would refuse
 * 4K; a higher one is refused by some encoders at 1080p.
 */
export const VIDEO_CODECS: readonly VideoCodecCandidate[] = [
  { name: 'H.264', codec: 'avc1.640028', container: 'mp4' },
  { name: 'H.264 baseline', codec: 'avc1.42001f', container: 'mp4' },
  { name: 'VP9', codec: 'vp09.00.10.08', container: 'webm' },
  { name: 'AV1', codec: 'av01.0.04M.08', container: 'webm' },
];

/** Answers whether the host can encode with a given configuration. */
export type CodecProbe = (config: VideoEncodeConfig) => Promise<boolean>;

export interface CodecSelection {
  readonly candidate: VideoCodecCandidate;
  readonly config: VideoEncodeConfig;
  /** Everything tried before this one, and why it was passed over. */
  readonly rejected: readonly { readonly name: string; readonly reason: string }[];
}

/**
 * Picks a codec the host will actually accept.
 *
 * A fixed codec string is the usual way this is written and it is wrong twice
 * over: it fails outright on a browser without that encoder, and it silently
 * picks a software path on a machine that had a hardware one for something else.
 * Asking is cheap — one `isConfigSupported` per candidate, once per export.
 *
 * A probe that throws is treated as "no", because that is what a browser that
 * dislikes the string actually does.
 */
export async function selectVideoCodec(options: {
  readonly probe: CodecProbe;
  readonly width: number;
  readonly height: number;
  readonly framerate: number;
  readonly bitrate: number;
  readonly candidates?: readonly VideoCodecCandidate[];
  /** Restricts the search, for a caller that wants a specific container. */
  readonly container?: 'mp4' | 'webm';
}): Promise<CodecSelection | null> {
  const candidates = (options.candidates ?? VIDEO_CODECS).filter(
    (entry) => options.container === undefined || entry.container === options.container,
  );
  const rejected: { name: string; reason: string }[] = [];

  for (const candidate of candidates) {
    const config: VideoEncodeConfig = {
      codec: candidate.codec,
      width: options.width,
      height: options.height,
      framerate: options.framerate,
      bitrate: options.bitrate,
      hardwareAcceleration: 'no-preference',
      latencyMode: 'quality',
    };
    let supported = false;
    try {
      supported = await options.probe(config);
    } catch (cause) {
      rejected.push({
        name: candidate.name,
        reason: cause instanceof Error ? cause.message : String(cause),
      });
      continue;
    }
    if (supported) return { candidate, config, rejected };
    rejected.push({ name: candidate.name, reason: 'not supported' });
  }
  return null;
}

/**
 * A sensible bitrate for a frame size and rate.
 *
 * Roughly 0.1 bits per pixel per frame, which is a common target for
 * high-quality screen-like content, clamped so a 720p export is not starved and
 * a 4K one does not ask for something no encoder will honour. This is a default,
 * not a policy — a caller may pass whatever it likes.
 */
export function suggestedBitrate(width: number, height: number, framerate: number): number {
  const raw = width * height * framerate * 0.1;
  return Math.round(Math.min(120_000_000, Math.max(2_000_000, raw)));
}

/**
 * The timestamp of a frame, in microseconds.
 *
 * From the frame index and the frame rate, and from nothing else. This is the
 * same rule `OfflineRenderClock` follows for time in seconds and it exists for
 * the same reason: a timestamp taken from a wall clock makes the exported video
 * depend on how fast the machine happened to be, and two exports of one
 * performance would then have different timing.
 *
 * The multiplication happens before the division so the arithmetic stays on
 * integers for as long as possible — at 30 fps a per-frame increment of 33 333
 * accumulates a third of a millisecond of drift every second, and over four
 * minutes that is a frame and a half.
 */
export function frameTimestampMicros(frameIndex: number, fps: number): number {
  if (!Number.isFinite(fps) || fps <= 0) return 0;
  return Math.round((frameIndex * 1_000_000) / fps);
}

/** How long one frame lasts, in microseconds. */
export function frameDurationMicros(fps: number): number {
  if (!Number.isFinite(fps) || fps <= 0) return 0;
  return Math.round(1_000_000 / fps);
}
